import React, { useState, useCallback, useRef } from 'react';
import { Upload, X, FileText, Image, File, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const FILE_ICONS = {
  pdf: FileText,
  jpg: Image,
  jpeg: Image,
  png: Image,
  doc: FileText,
  docx: FileText,
  xls: FileText,
  xlsx: FileText,
  default: File
};

const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];

const MAX_SIZE = 10 * 1024 * 1024; // 10MB

export default function FileUpload({
  caseId,
  checklistItemName,
  onUploadSuccess,
  onCancel,
  existingFile = null,
  // Family context — all optional; default to primary applicant
  applicantType = 'primary',
  applicantId = null,
  applicantName = null,
  documentSideKey = null,
  documentSideLabel = null,
  customRequestId = null,
}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const getFileExtension = (filename) => {
    return filename.split('.').pop().toLowerCase();
  };

  const getFileIcon = (filename) => {
    const ext = getFileExtension(filename);
    return FILE_ICONS[ext] || FILE_ICONS.default;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload PDF, JPG, PNG, DOC, DOCX, XLS, or XLSX files.';
    }
    if (file.size > MAX_SIZE) {
      return 'File too large. Maximum size is 10MB.';
    }
    return null;
  };

  const handleFile = useCallback((selectedFile) => {
    setError(null);
    
    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }

    setFile(selectedFile);

    // Create preview for images
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFile(droppedFiles[0]);
    }
  }, [handleFile]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !caseId || !checklistItemName) return;

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('case_id', caseId);
    formData.append('checklist_item_name', checklistItemName);
    formData.append('applicant_type', applicantType);
    if (applicantId)       formData.append('applicant_id', applicantId);
    if (applicantName)     formData.append('applicant_name', applicantName);
    if (documentSideKey)   formData.append('document_side_key', documentSideKey);
    if (documentSideLabel) formData.append('document_side_label', documentSideLabel);
    if (customRequestId)   formData.append('custom_request_id', customRequestId);

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Simulate progress (since fetch doesn't support progress)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      // Use the correct endpoint that saves to database
      const response = await fetch(`${API_URL}/api/documents/upload-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      setUploadProgress(100);
      const data = await response.json();
      
      toast.success('Document uploaded successfully!');
      
      if (onUploadSuccess) {
        onUploadSuccess(data);
      }
    } catch (err) {
      setError(err.message || 'Failed to upload file');
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const FileIcon = file ? getFileIcon(file.name) : Upload;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="file-upload-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-emerald-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold">Upload Document</h3>
              <p className="text-teal-100 text-sm mt-1">
                {checklistItemName}{documentSideLabel ? ` — ${documentSideLabel}` : ''}
              </p>
              {applicantName && applicantType !== 'primary' && (
                <p className="text-teal-200 text-xs mt-0.5">
                  For: {applicantName} ({applicantType === 'spouse' ? 'Spouse' : 'Child'})
                </p>
              )}
            </div>
            <button
              onClick={onCancel}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="close-upload-modal"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Drop Zone */}
          {!file && (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                isDragging 
                  ? 'border-teal-500 bg-teal-50' 
                  : 'border-slate-300 hover:border-teal-400 hover:bg-slate-50'
              }`}
              data-testid="drop-zone"
            >
              <Upload 
                size={48} 
                className={`mx-auto mb-4 ${isDragging ? 'text-teal-500' : 'text-slate-400'}`} 
              />
              <p className="text-lg font-medium text-slate-700">
                {isDragging ? 'Drop your file here' : 'Drag & drop your file here'}
              </p>
              <p className="text-slate-500 mt-2">or click to browse</p>
              <p className="text-xs text-slate-400 mt-4">
                Supported: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX (Max 10MB)
              </p>
            </div>
          )}

          {/* File Preview */}
          {file && (
            <div className="border border-slate-200 rounded-xl p-4" data-testid="file-preview">
              <div className="flex items-start gap-4">
                {/* Thumbnail or Icon */}
                <div className="flex-shrink-0">
                  {preview ? (
                    <img 
                      src={preview} 
                      alt="Preview" 
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center">
                      <FileIcon size={32} className="text-slate-500" />
                    </div>
                  )}
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate" title={file.name}>
                    {file.name}
                  </p>
                  <p className="text-sm text-slate-500">{formatFileSize(file.size)}</p>
                  
                  {/* Progress Bar */}
                  {uploading && (
                    <div className="mt-3">
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div 
                          className="bg-teal-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{uploadProgress}% uploaded</p>
                    </div>
                  )}

                  {/* Success */}
                  {uploadProgress === 100 && !uploading && (
                    <div className="flex items-center gap-2 mt-2 text-green-600">
                      <CheckCircle size={16} />
                      <span className="text-sm">Upload complete!</span>
                    </div>
                  )}
                </div>

                {/* Remove Button */}
                {!uploading && uploadProgress !== 100 && (
                  <button
                    onClick={handleRemoveFile}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                    data-testid="remove-file-button"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="upload-error">
              <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Hidden File Input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
            className="hidden"
            data-testid="file-input"
          />
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
            disabled={uploading}
            data-testid="cancel-upload-button"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || uploading || uploadProgress === 100}
            className="flex-1 px-4 py-3 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="confirm-upload-button"
          >
            {uploading ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Uploading...
              </>
            ) : uploadProgress === 100 ? (
              <>
                <CheckCircle size={20} />
                Done
              </>
            ) : (
              <>
                <Upload size={20} />
                Upload Document
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
